import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

export interface BookingConflict {
  booking_id: string;
  unit_id: string;
  organization_id: string;
  host_id: string;
  guest_name: string;
  check_in: string;
  check_out: string;
  status: string;
  platform: string | null;
  synced_from_ical: boolean;
  conflicting_booking_id: string;
  conflicting_guest_name: string;
  conflicting_check_in: string;
  conflicting_check_out: string;
  conflicting_status: string;
  conflicting_platform: string | null;
  conflicting_synced_from_ical: boolean;
}

/** Booking conflicts scoped to a single host's own bookings (Host Dashboard). */
export function useHostBookingConflicts(hostId: string | undefined) {
  return useQuery({
    queryKey: ["booking_conflicts", "host", hostId],
    enabled: !!hostId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_conflicts")
        .select("*")
        .eq("host_id", hostId!);
      if (error) throw error;
      return dedupeConflictPairs(data as BookingConflict[]);
    },
  });
}

/** Booking conflicts across the whole org (admin/PM Bookings page). */
export function useOrgBookingConflicts() {
  const { activeOrgId } = useAuth();
  return useQuery({
    queryKey: ["booking_conflicts", "org", activeOrgId],
    enabled: !!activeOrgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_conflicts")
        .select("*")
        .eq("organization_id", activeOrgId!);
      if (error) throw error;
      return dedupeConflictPairs(data as BookingConflict[]);
    },
  });
}

// The view returns each conflicting pair twice (A,B) and (B,A) — keep only
// one direction per pair, since the JS query builder can't express a
// column-vs-column "WHERE booking_id < conflicting_booking_id" filter.
function dedupeConflictPairs(rows: BookingConflict[]): BookingConflict[] {
  const seen = new Set<string>();
  const result: BookingConflict[] = [];
  for (const row of rows) {
    const key = [row.booking_id, row.conflicting_booking_id].sort().join(":");
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(row);
  }
  return result;
}

/** Resolve a conflict by cancelling one of the two bookings in the pair. */
export function useResolveBookingConflict() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (bookingIdToCancel: string) => {
      const { error } = await supabase
        .from("bookings")
        .update({ status: "cancelled", updated_at: new Date().toISOString() })
        .eq("id", bookingIdToCancel);
      if (error) throw error;

      await supabase.from("audit_logs").insert({
        action: "Booking conflict resolved",
        entity: `Booking ${bookingIdToCancel}`,
        detail: "Cancelled as a duplicate/overlapping booking",
        user_name: user?.fullName ?? "Admin",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking_conflicts"] });
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      toast({ title: "Conflict resolved", description: "The duplicate booking was cancelled." });
    },
    onError: (error: Error) => {
      toast({ title: "Could not resolve conflict", description: error.message, variant: "destructive" });
    },
  });
}
